import { useState, useEffect } from 'react';
import {
  Shield,
  Code,
  GitBranch,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Bot,
  Target,
  Key,
  Rocket,
  Clock,
  DollarSign,
  Award,
  Play,
  ChevronRight,
  Lock,
  Workflow,
  Brain,
  Gauge,
} from 'lucide-react';
import { CycloneLogo } from '../CycloneLogo';
import { AccessCodeModal } from './AccessCodeModal';

interface LandingPageEnhancedProps {
  onGetStarted: () => void;
  onJoinWaitlist: () => void;
}

export function LandingPageEnhanced({
  onGetStarted,
  onJoinWaitlist,
}: LandingPageEnhancedProps) {
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [_activeTab, setActiveTab] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate tabs for demo preview
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTab(prev => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const stats = [
    { value: '10x', label: 'Faster Development', icon: Rocket },
    { value: '85%', label: 'Cost Reduction', icon: DollarSign },
    { value: '99.9%', label: 'Code Quality', icon: Award },
    { value: '24/7', label: 'AI Agents Working', icon: Clock },
  ];

  const phases = [
    {
      number: '01',
      title: 'Plan Mode',
      description:
        'AI analyzes your GitHub repo, generates comprehensive blueprints, and maps your entire SDLC',
      features: [
        'GitHub repository analysis',
        'Tech stack detection',
        'Architecture visualization',
        'Gap analysis & recommendations',
      ],
      color: 'blue',
    },
    {
      number: '02',
      title: 'Build Mode',
      description:
        'Autonomous AI agents execute your project with 25+ specialized roles working in parallel',
      features: [
        '25+ specialized AI agents',
        'Real-time code generation',
        'Automated testing & QA',
        'GitHub PR management',
      ],
      color: 'purple',
    },
    {
      number: '03',
      title: 'Deploy & Monitor',
      description:
        'Quality gates, compliance checks, and production deployment with continuous monitoring',
      features: [
        'Automated deployment',
        'Quality gate enforcement',
        'DORA metrics tracking',
        'Real-time monitoring',
      ],
      color: 'green',
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-950 via-slate-950 to-slate-950 relative overflow-hidden'>
      {/* Animated background elements */}
      <div className='absolute inset-0 overflow-hidden'>
        <div className='absolute w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-3xl top-20 -left-20 animate-pulse' />
        <div className='absolute w-[500px] h-[500px] bg-accent-500/20 rounded-full blur-3xl bottom-20 -right-20 animate-pulse delay-1000' />
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-slate-900/95 backdrop-blur-lg border-b border-slate-800/50 shadow-xl'
            : 'bg-transparent'
        }`}
      >
        <div className='container mx-auto px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <CycloneLogo size={40} />
              <div>
                <span className='text-2xl font-bold text-white'>Cyclone</span>
                <span className='ml-2 text-xs text-accent-400 font-semibold px-2 py-1 bg-accent-500/20 rounded'>
                  BETA
                </span>
              </div>
            </div>
            <div className='flex items-center gap-4'>
              <button
                onClick={() => setShowAccessCodeModal(true)}
                className='px-6 py-2 text-white hover:text-accent-300 transition-colors font-medium flex items-center gap-2'
              >
                <Key className='w-4 h-4' />
                Access Code
              </button>
              <button
                onClick={onJoinWaitlist}
                className='btn btn-primary px-6 py-2.5 shadow-lg hover:shadow-xl font-semibold'
              >
                Join Waitlist
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className='relative pt-32 pb-20 lg:pt-40 lg:pb-32'>
        <div className='container mx-auto px-6'>
          <div className='max-w-6xl mx-auto'>
            {/* Badge */}
            <div className='flex justify-center mb-8'>
              <div className='inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-accent-500/20 to-accent-500/20 border border-accent-500/30 rounded-full backdrop-blur-sm'>
                <Sparkles className='w-4 h-4 text-accent-400 animate-pulse' />
                <span className='text-accent-300 text-sm font-semibold'>
                  Your Autonomous AI Development Agency
                </span>
                <ChevronRight className='w-4 h-4 text-accent-400' />
              </div>
            </div>

            {/* Main Headline */}
            <h1 className='text-5xl lg:text-7xl xl:text-8xl font-bold text-center mb-8 leading-tight'>
              <span className='text-white'>Ship Software</span>
              <br />
              <span className='bg-gradient-to-r from-accent-400 to-accent-400 bg-clip-text text-transparent animate-gradient-x'>
                10x Faster
              </span>
              <br />
              <span className='text-white'>With AI Agents</span>
            </h1>

            {/* Subheadline */}
            <p className='text-xl lg:text-2xl text-text-secondary text-center mb-12 max-w-4xl mx-auto leading-relaxed'>
              From concept to production in hours, not months. Connect your
              GitHub, define requirements, and watch{' '}
              <strong>25+ specialized AI agents</strong> build, test, and deploy
              your entire application autonomously.
            </p>

            {/* CTA Buttons */}
            <div className='flex flex-col sm:flex-row gap-4 justify-center mb-16'>
              <button
                onClick={onJoinWaitlist}
                className='btn-primary group px-10 py-5 text-lg rounded-xl transition-all shadow-2xl hover:shadow-primary/50 font-bold inline-flex items-center justify-center gap-3'
              >
                <span>Start Building with AI</span>
                <ArrowRight className='w-6 h-6 group-hover:translate-x-2 transition-transform' />
              </button>
              <button
                onClick={() => {
                  /* TODO: Demo video */
                }}
                className='group px-10 py-5 bg-white/10 backdrop-blur-sm text-white text-lg rounded-xl hover:bg-white/20 transition-all border border-white/20 font-bold inline-flex items-center justify-center gap-3'
              >
                <Play className='w-6 h-6' />
                <span>Watch Demo</span>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className='flex flex-wrap items-center justify-center gap-8 text-sm text-text-secondary'>
              {[
                { icon: CheckCircle, text: '25+ AI Agents' },
                { icon: CheckCircle, text: 'GitHub Integration' },
                { icon: CheckCircle, text: 'Enterprise Security' },
                { icon: CheckCircle, text: 'BYOK Model' },
                { icon: Lock, text: 'SOC 2 Compliant' },
              ].map((item, idx) => (
                <div key={idx} className='flex items-center gap-2'>
                  <item.icon className='w-4 h-4 text-success' />
                  <span className='font-medium'>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Floating Animation Cards */}
        <div className='absolute top-1/4 left-10 hidden xl:block'>
          <div className='w-72 bg-gradient-to-br from-accent-500/20 to-accent-500/5 backdrop-blur-xl rounded-2xl border border-accent-500/30 p-4 shadow-2xl animate-float'>
            <div className='flex items-start gap-3'>
              <div className='w-10 h-10 bg-accent-500/20 rounded-lg flex items-center justify-center'>
                <Bot className='w-6 h-6 text-accent-400' />
              </div>
              <div className='flex-1'>
                <div className='text-white font-semibold mb-1'>
                  Frontend Agent
                </div>
                <div className='text-sm text-text-secondary'>
                  Building React components...
                </div>
                <div className='mt-2 w-full bg-accent-900/30 rounded-full h-1.5'>
                  <div className='bg-accent-500 h-1.5 rounded-full w-3/4 animate-pulse' />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='absolute top-1/3 right-10 hidden xl:block'>
          <div className='w-72 bg-gradient-to-br from-accent-500/20 to-accent-500/5 backdrop-blur-xl rounded-2xl border border-accent-500/30 p-4 shadow-2xl animate-float-delayed'>
            <div className='flex items-start gap-3'>
              <div className='w-10 h-10 bg-accent-500/20 rounded-lg flex items-center justify-center'>
                <Code className='w-6 h-6 text-accent-400' />
              </div>
              <div className='flex-1'>
                <div className='text-white font-semibold mb-1'>
                  Code Generated
                </div>
                <div className='text-sm text-text-secondary'>1,247 lines in 3.2s</div>
                <div className='mt-2 flex gap-1'>
                  <div className='w-2 h-2 bg-success rounded-full animate-pulse' />
                  <span className='text-xs text-success'>Tests passing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className='py-16 relative'>
        <div className='container mx-auto px-6'>
          <div className='max-w-6xl mx-auto'>
            <div className='grid grid-cols-2 lg:grid-cols-4 gap-6'>
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className='bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 text-center hover:scale-105 transition-transform'
                >
                  <stat.icon className='w-8 h-8 text-accent-400 mx-auto mb-3' />
                  <div className='text-4xl font-bold text-white mb-2'>
                    {stat.value}
                  </div>
                  <div className='text-sm text-text-secondary'>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className='py-20 relative'>
        <div className='container mx-auto px-6'>
          <div className='max-w-6xl mx-auto'>
            <div className='text-center mb-16'>
              <h2 className='text-5xl font-bold text-white mb-4'>
                How Cyclone Works
              </h2>
              <p className='text-xl text-text-secondary'>
                From idea to deployment in three simple phases
              </p>
            </div>

            <div className='space-y-12'>
              {phases.map((phase, idx) => (
                <div
                  key={idx}
                  className='group relative bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-xl rounded-3xl border border-slate-700/50 p-8 lg:p-12 hover:border-accent-500/50 transition-all'
                >
                  {/* Phase number */}
                  <div className='absolute -top-6 left-8'>
                    <div className='w-16 h-16 bg-gradient-to-br from-accent-600 to-accent-500 rounded-2xl flex items-center justify-center shadow-lg'>
                      <span className='text-white font-bold text-xl'>
                        {phase.number}
                      </span>
                    </div>
                  </div>

                  <div className='grid lg:grid-cols-2 gap-8 items-center'>
                    <div>
                      <h3 className='text-3xl font-bold text-white mb-4'>
                        {phase.title}
                      </h3>
                      <p className='text-lg text-text-secondary mb-6'>
                        {phase.description}
                      </p>
                      <ul className='space-y-3'>
                        {phase.features.map((feature, featureIdx) => (
                          <li
                            key={featureIdx}
                            className='flex items-center gap-3 text-text-secondary'
                          >
                            <CheckCircle className='w-5 h-5 text-success flex-shrink-0' />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Visual placeholder - in production, add actual screenshots */}
                    <div className='bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-2xl p-8 border border-slate-700/50 h-64 flex items-center justify-center'>
                      <div className='text-center'>
                        <Workflow className='w-16 h-16 text-primary mx-auto mb-4 opacity-50' />
                        <p className='text-text-tertiary text-sm'>
                          {phase.title} Preview
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className='py-20 relative bg-slate-900/50'>
        <div className='container mx-auto px-6'>
          <div className='max-w-6xl mx-auto'>
            <div className='text-center mb-16'>
              <h2 className='text-5xl font-bold text-white mb-4'>
                Everything You Need
              </h2>
              <p className='text-xl text-text-secondary'>
                Enterprise-grade features for modern development teams
              </p>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {[
                {
                  icon: Brain,
                  title: '25+ AI Agents',
                  description:
                    'Specialized agents for every role: architects, developers, QA, DevOps',
                },
                {
                  icon: GitBranch,
                  title: 'GitHub Native',
                  description:
                    'Deep integration with GitHub: PRs, commits, code reviews',
                },
                {
                  icon: Shield,
                  title: 'Enterprise Security',
                  description:
                    'SOC 2 compliant, BYOK model, end-to-end encryption',
                },
                {
                  icon: Gauge,
                  title: 'DORA Metrics',
                  description:
                    'Track deployment frequency, lead time, change failure rate',
                },
                {
                  icon: Target,
                  title: 'Jira Sync',
                  description:
                    'Bi-directional sync for seamless project management',
                },
                {
                  icon: Rocket,
                  title: 'Auto Deployment',
                  description:
                    'CI/CD pipelines, quality gates, automated rollbacks',
                },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className='bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-6 hover:border-primary/50 transition-all group'
                >
                  <div className='w-12 h-12 bg-accent-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-accent-500/30 transition-colors'>
                    <feature.icon className='w-6 h-6 text-accent-400' />
                  </div>
                  <h3 className='text-xl font-bold text-white mb-2'>
                    {feature.title}
                  </h3>
                  <p className='text-gray-400'>{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className='py-20 relative'>
        <div className='container mx-auto px-6'>
          <div className='max-w-4xl mx-auto'>
            <div className='bg-gradient-to-r from-accent-500/20 to-accent-500/20 backdrop-blur-xl rounded-3xl border border-accent-500/30 p-12 text-center'>
              <h2 className='text-4xl lg:text-5xl font-bold text-white mb-6'>
                Ready to Transform Your Development?
              </h2>
              <p className='text-xl text-gray-300 mb-8 max-w-2xl mx-auto'>
                Join forward-thinking teams who are shipping 10x faster with AI
                agents. Limited beta access available.
              </p>
              <button
                onClick={onJoinWaitlist}
                className='btn btn-primary px-10 py-5 text-lg rounded-xl shadow-2xl hover:shadow-xl font-bold inline-flex items-center gap-3'
              >
                <span>Join the Waitlist</span>
                <ArrowRight className='w-6 h-6' />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='py-12 border-t border-slate-800/50'>
        <div className='container mx-auto px-6'>
          <div className='flex flex-col md:flex-row items-center justify-between'>
            <div className='flex items-center gap-3 mb-4 md:mb-0'>
              <CycloneLogo size={32} />
              <span className='text-lg font-bold text-white'>Cyclone</span>
            </div>
            <div className='text-gray-400 text-sm'>
              © 2025 Cyclone AI. All rights reserved.
            </div>
          </div>
        </div>
      </footer>

      {/* Access Code Modal */}
      {showAccessCodeModal && (
        <AccessCodeModal
          onClose={() => setShowAccessCodeModal(false)}
          onSuccess={onGetStarted}
        />
      )}

      {/* Custom animations */}
      <style>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float 6s ease-in-out infinite;
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}
